import {
  Box,
  Card,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ExpandableImage from "../../../../../components/ExpandableImage";
import usePresignedUrl from "../../../../../hooks/usePresignedUrl";
import { useTheme } from "@emotion/react";
import { Close } from "@mui/icons-material";
import TextFieldDebouncedNoInitialLoad from "../../../../../components/TextFieldDebouncedNoInitialLoad";
import { toTwoDecimals } from "../../../../../components/NewDetail/calculations/utils";
import AdornedInputDebouncedNoInitialLoad from "../../../../../components/AdornedInputDebouncedNoInitialLoad";

const TrimPiece = ({
  isTollForm,
  trimType,
  piece,
  onUpdatePiece,
  onRemovePiece,
}) => {
  const theme = useTheme();
  let pieceKey = "";
  switch (trimType) {
    case "TrimAndFlashing":
      pieceKey = "trim";
      break;
    case "CopingCap":
      pieceKey = "cap";
      break;
    case "CopingCapCleat":
      pieceKey = "cleat";
      break;
    case "BenchWork":
      pieceKey = "benchPiece";
      break;
    case "SplicePlate":
      pieceKey = "plate";
      break;
  }

  const presignedUrl = usePresignedUrl(piece[pieceKey]?.image);

  return (
    <Box
      sx={{
        padding: "16px",
        display: "flex",
        flexDirection: "row",
        position: "relative",
        marginBottom: "8px",
      }}
    >
      {trimType !== "SplicePlate" && (
        <ExpandableImage
          src={presignedUrl}
          imageTitle={piece[pieceKey]?.name}
          thumbnailHeight={100}
          thumbnailWidth={100}
        />
      )}

      <Box sx={{ marginLeft: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          {/* Name */}
          {trimType !== "SplicePlate" && (
            <TextFieldDebouncedNoInitialLoad
              sx={{ flex: 1 }}
              size="small"
              label="Name"
              value={piece[pieceKey]?.name}
              onChange={(e) => {
                onUpdatePiece({
                  ...piece,
                  name: e.target.value,
                });
              }}
            />
          )}
          {/* Flats Used -- BenchWork */}
          {trimType == "BenchWork" && (
            <TextFieldDebouncedNoInitialLoad
              size="small"
              sx={{
                marginLeft: "8px",
                width: "150px",
                fieldset: {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 3,
                },
              }}
              type="number"
              onFocus={(e) => e.target.select()}
              label="Flat Sheets Needed"
              value={piece.variables.flats_used ?? 0}
              onChange={(e) => {
                // ONLY FOR BENCHWORK: Directly modify calculations estimated_labor_hours
                let variablesCopy = {
                  ...piece.variables,
                  flats_used: e.target.value,
                };

                onUpdatePiece({
                  ...piece,
                  variables: variablesCopy,
                });
              }}
            />
          )}
          {trimType == "BenchWork" && (
            <TextFieldDebouncedNoInitialLoad
              size="small"
              sx={{
                marginLeft: "8px",
                width: "100px",
                fieldset: {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 3,
                },
              }}
              type="number"
              onFocus={(e) => e.target.select()}
              label="Est. Labor Hours"
              value={piece.variables.estimated_labor_hours ?? 0}
              onChange={(e) => {
                // ONLY FOR BENCHWORK: Directly modify calculations estimated_labor_hours
                let variablesCopy = {
                  ...piece.variables,
                  estimated_labor_hours: e.target.value,
                };

                onUpdatePiece({
                  ...piece,
                  variables: variablesCopy,
                });
              }}
            />
          )}
          {trimType == "BenchWork" && isTollForm && (
            <TextFieldDebouncedNoInitialLoad
              size="small"
              sx={{
                marginLeft: "8px",
                width: "150px",
                fieldset: {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 3,
                },
              }}
              type="number"
              onFocus={(e) => e.target.select()}
              label="Est. Labor Cost"
              value={piece.variables.bench_work_toll_form_labor_cost ?? 0}
              onChange={(e) => {
                // ONLY FOR BENCHWORK: Directly modify calculations estimated_labor_hours
                let variablesCopy = {
                  ...piece.variables,
                  bench_work_toll_form_labor_cost: e.target.value,
                };

                onUpdatePiece({
                  ...piece,
                  variables: variablesCopy,
                });
              }}
            />
          )}

          {/* Quantity */}
          {trimType == "SplicePlate" && (
            <TextFieldDebouncedNoInitialLoad
              sx={{
                width: "100px",
                fieldset: {
                  borderColor:
                    trimType != "SplicePlate" && piece[pieceKey].isMasterCatalog
                      ? null
                      : theme.palette.primary.main,
                  borderWidth: 3,
                },
              }}
              size="small"
              type="number"
              onFocus={(e) => e.target.select()}
              label="Quantity"
              value={piece.quantity}
              onChange={(e) => {
                onUpdatePiece({
                  ...piece,
                  quantity: parseFloat(e.target.value).toFixed(0),
                });
              }}
            />
          )}
          {/* Stretchout */}
          {trimType !== "BenchWork" && (
            <TextFieldDebouncedNoInitialLoad
              size="small"
              sx={{
                marginLeft: "8px",
                width: "100px",
                fieldset: {
                  borderColor:
                    trimType != "SplicePlate" && piece[pieceKey].isMasterCatalog
                      ? null
                      : theme.palette.primary.main,
                  borderWidth: 3,
                },
              }}
              type="number"
              onFocus={(e) => e.target.select()}
              label="Stretch Out"
              value={piece.stretchOut}
              disabled={
                trimType != "SplicePlate" && piece[pieceKey].isMasterCatalog
              }
              onChange={(e) => {
                onUpdatePiece({
                  ...piece,
                  stretchOut: e.target.value,
                });
              }}
            />
          )}

          {/* Hits */}
          {trimType !== "BenchWork" && (
            <TextFieldDebouncedNoInitialLoad
              size="small"
              sx={{
                marginLeft: "8px",
                width: "100px",
                fieldset: {
                  borderColor:
                    trimType != "SplicePlate" && piece[pieceKey].isMasterCatalog
                      ? null
                      : theme.palette.primary.main,
                  borderWidth: 3,
                },
              }}
              type="number"
              onFocus={(e) => e.target.select()}
              label="Hits"
              value={piece.hits}
              disabled={
                trimType != "SplicePlate" && piece[pieceKey].isMasterCatalog
              }
              onChange={(e) => {
                onUpdatePiece({
                  ...piece,
                  hits: parseFloat(e.target.value).toFixed(0),
                });
              }}
            />
          )}

          {/* Splice Plate Width */}
          {trimType == "SplicePlate" && (
            <TextField
              select
              label="Width"
              value={parseFloat(piece.length ?? 0)}
              onChange={(e) => {
                onUpdatePiece({
                  ...piece,
                  length: e.target.value,
                });
              }}
              size="small"
              sx={{
                marginLeft: "8px",
                width: "100px",
                fieldset: {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 3,
                },
              }}
            >
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={6}>6</MenuItem>
              <MenuItem value={8}>8</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={12}>12</MenuItem>
            </TextField>
          )}
        </Box>

        {piece.calculations && (
          <Box
            sx={{ marginTop: "16px", display: "flex", flexDirection: "row" }}
          >
            {/* Quantity */}
            {trimType != "SplicePlate" && (
              <TextFieldDebouncedNoInitialLoad
                sx={{ flex: 1 }}
                size="small"
                type="number"
                onFocus={(e) => e.target.select()}
                label="Quantity"
                value={piece.quantity}
                onChange={(e) => {
                  onUpdatePiece({
                    ...piece,
                    quantity: parseFloat(e.target.value).toFixed(0),
                  });
                }}
              />
            )}

            {/* Flats Used - SplicePlate*/}
            {trimType == "SplicePlate" && (
              <TextFieldDebouncedNoInitialLoad
                size="small"
                sx={{
                  width: "100px",
                }}
                disabled
                type="number"
                onFocus={(e) => e.target.select()}
                label="Flat Sheets Needed"
                value={piece.calculations.flatSheetsNeeded ?? 0}
              />
            )}

            {/* Suggested Per Piece Price */}
            <TextFieldDebouncedNoInitialLoad
              sx={{ flex: 1, marginLeft: "8px" }}
              disabled
              size="small"
              type="number"
              onFocus={(e) => e.target.select()}
              label="Suggested Price Per Piece"
              value={piece.calculations.pricePerPiece}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="Start" sx={{ marginRight: "4px" }}>
                    $
                  </InputAdornment>
                ),
              }}
            />
            {/* Suggested Total Price */}
            <TextFieldDebouncedNoInitialLoad
              sx={{ flex: 1, marginLeft: "8px" }}
              disabled
              size="small"
              type="number"
              onFocus={(e) => e.target.select()}
              label="Suggested Sale Price"
              value={piece.calculations.totalSellPrice}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="Start" sx={{ marginRight: "4px" }}>
                    $
                  </InputAdornment>
                ),
              }}
            />
            {/* Price Per Piece */}
            <Box sx={{ width: "150px", marginLeft: "8px" }}>
              <AdornedInputDebouncedNoInitialLoad
                type="number"
                onWheel={(event) => {
                  event.preventDefault();
                }}
                onFocus={(e) => e.target.select()}
                label="Final Price Per Piece"
                value={toTwoDecimals(
                  piece.pricePerPiece ? piece.pricePerPiece : 0
                )}
                onChange={(e) => {
                  let pieceQuantity = Number(piece.quantity);
                  let price_per_piece_override = Number(e.target.value ?? 0);
                  let total_sell_price_override =
                    price_per_piece_override * pieceQuantity;
                  let overrides = {
                    ...piece.overrides,
                    price_per_piece: price_per_piece_override,
                    total_sell_price: total_sell_price_override,
                  };
                  onUpdatePiece({
                    ...piece,
                    overrides,
                  });
                }}
                isOveridden={
                  (piece.overrides &&
                    piece.overrides.price_per_piece !== null &&
                    piece.overrides.price_per_piece !== undefined) ||
                  (piece.overrides &&
                    piece.overrides.total_sell_price !== null &&
                    piece.overrides.total_sell_price !== undefined)
                }
                handleReset={() => {
                  let overrides = {
                    ...piece.overrides,
                    price_per_piece: null,
                    total_sell_price: null,
                  };
                  onUpdatePiece({
                    ...piece,
                    overrides,
                  });
                }}
                symbol="$"
                position="start"
              />
            </Box>

            {/* Total Sell Price */}
            <Box sx={{ width: "200px", marginLeft: "8px" }}>
              <AdornedInputDebouncedNoInitialLoad
                type="number"
                onWheel={(event) => {
                  event.preventDefault();
                }}
                onFocus={(e) => e.target.select()}
                label="Final Sale Price"
                value={toTwoDecimals(
                  piece.totalSellPrice ? piece.totalSellPrice : 0
                )}
                onChange={(e) => {
                  let pieceQuantity = Number(piece.quantity);

                  let total_sell_price_override = Number(e.target.value ?? 0);
                  let price_per_piece_override =
                    total_sell_price_override / pieceQuantity;
                  let overrides = {
                    ...piece.overrides,
                    price_per_piece: price_per_piece_override,
                    total_sell_price: total_sell_price_override,
                  };
                  onUpdatePiece({
                    ...piece,
                    overrides,
                  });
                }}
                isOveridden={
                  (piece.overrides &&
                    piece.overrides.price_per_piece !== null &&
                    piece.overrides.price_per_piece !== undefined) ||
                  (piece.overrides &&
                    piece.overrides.total_sell_price !== null &&
                    piece.overrides.total_sell_price !== undefined)
                }
                handleReset={() => {
                  let overrides = {
                    ...piece.overrides,
                    price_per_piece: null,
                    total_sell_price: null,
                  };
                  onUpdatePiece({
                    ...piece,
                    overrides,
                  });
                }}
                symbol="$"
                position="start"
              />
            </Box>
          </Box>
        )}
      </Box>

      <IconButton
        sx={{ position: "absolute", top: 0, right: 0 }}
        onClick={onRemovePiece}
      >
        <Close />
      </IconButton>
    </Box>
  );
};

export default TrimPiece;
